/**
 * MusicServiceBookingFlow - 5-step booking wizard for music services
 * Flow: Service → Details → Schedule → Contact → Payment
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Calendar } from '../../components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Music, ChevronRight, ChevronLeft, CheckCircle, MapPin, Clock,
  User, Mail, Phone, CreditCard, Loader2, Star, Home, Video,
  Building, Calendar as CalendarIcon
} from 'lucide-react';
import { useCountry } from '../../context/CountryContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Steps
const STEPS = [
  { id: 1, name: 'Service', icon: Music },
  { id: 2, name: 'Details', icon: User },
  { id: 3, name: 'Schedule', icon: CalendarIcon },
  { id: 4, name: 'Contact', icon: Phone },
  { id: 5, name: 'Payment', icon: CreditCard },
];

// Service Types
const SERVICE_TYPES = {
  lessons: { name: 'Music Lessons', icon: '🎵', durations: [30, 45, 60, 90] },
  repair: { name: 'Repair & Setup', icon: '🔧', durations: [60, 120] },
  tuning: { name: 'Piano Tuning', icon: '🎹', durations: [60, 90, 120] },
  cleaning: { name: 'Instrument Cleaning', icon: '✨', durations: [30, 60] },
  moving: { name: 'Instrument Moving', icon: '🚚', durations: [120, 180, 240] },
  setup: { name: 'Studio Setup', icon: '🎧', durations: [120, 180] },
};

// Location Types
const LOCATION_TYPES = [
  { id: 'provider', name: 'At Provider Location', icon: Building, description: 'Visit the provider\'s studio/shop' },
  { id: 'customer_home', name: 'Home Visit', icon: Home, description: 'Provider comes to you (+20%)' },
  { id: 'online', name: 'Online Session', icon: Video, description: 'Video call lesson' },
];

// Time Slots
const TIME_SLOTS = [
  '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
  '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
  '15:00', '15:30', '16:00', '16:30', '17:00', '17:30',
  '18:00', '18:30', '19:00', '19:30', '20:00',
];

// Step Progress Component
function StepProgress({ currentStep }) {
  return (
    <div className="mb-8">
      <div className="flex items-center justify-between">
        {STEPS.map((step, index) => {
          const Icon = step.icon;
          const isActive = currentStep === step.id;
          const isComplete = currentStep > step.id;
          
          return (
            <React.Fragment key={step.id}>
              <div className={`flex flex-col items-center ${isActive ? 'text-indigo-600' : isComplete ? 'text-green-600' : 'text-gray-400'}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  isActive ? 'bg-indigo-100 border-2 border-indigo-600' :
                  isComplete ? 'bg-green-100' : 'bg-gray-100'
                }`}>
                  {isComplete ? <CheckCircle className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                </div>
                <span className="text-xs mt-1 font-medium hidden sm:block">{step.name}</span>
              </div>
              {index < STEPS.length - 1 && (
                <div className={`flex-1 h-0.5 mx-2 ${isComplete ? 'bg-green-500' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

// Main Component
export default function MusicServiceBookingFlow() {
  const navigate = useNavigate();
  const { providerId } = useParams();
  const { country } = useCountry();
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [provider, setProvider] = useState(null);
  
  // Form data
  const [serviceType, setServiceType] = useState('');
  const [instrument, setInstrument] = useState('');
  const [duration, setDuration] = useState(60);
  const [locationType, setLocationType] = useState('provider');
  const [date, setDate] = useState(null);
  const [time, setTime] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [address, setAddress] = useState('');
  const [notes, setNotes] = useState('');
  
  // Pricing
  const currencySymbol = country === 'LK' ? 'Rs.' : '£';
  const basePrice = provider?.hourly_rate || (country === 'LK' ? 2000 : 35);
  const durationHours = duration / 60;
  const homeVisitSurcharge = locationType === 'customer_home' ? basePrice * 0.2 : 0;
  const totalPrice = (basePrice * durationHours) + homeVisitSurcharge;
  
  // Fetch provider
  useEffect(() => {
    const fetchProvider = async () => {
      if (!providerId) {
        setLoading(false);
        return;
      }
      
      try {
        const response = await fetch(`${API_URL}/api/music/providers/${providerId}`);
        const data = await response.json();
        
        if (data.success) {
          setProvider(data.provider);
          // Pre-select service based on provider type
          if (data.provider.provider_type === 'music_teacher') {
            setServiceType('lessons');
          } else if (data.provider.provider_type === 'repair_technician') {
            setServiceType('repair');
          } else if (data.provider.provider_type === 'piano_tuner') {
            setServiceType('tuning');
          }
        } else {
          toast.error('Provider not found');
          navigate('/music');
        }
      } catch (error) {
        console.error('Failed to fetch provider:', error);
        toast.error('Failed to load provider');
      } finally {
        setLoading(false);
      }
    };
    
    fetchProvider();
  }, [providerId, navigate]);
  
  // Handle booking submission
  const handleSubmit = async () => {
    setSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/music/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          provider_id: providerId,
          service_type: serviceType,
          instrument_type: instrument || null,
          customer_name: customerName,
          customer_email: customerEmail,
          customer_phone: customerPhone || null,
          appointment_date: date?.toISOString().split('T')[0],
          appointment_time: time,
          duration_minutes: duration,
          location_type: locationType,
          address: locationType === 'customer_home' ? address : null,
          notes: notes || null,
          country: country,
        }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Booking confirmed!');
        setStep(6); // Success step
      } else {
        toast.error(data.message || 'Booking failed');
      }
    } catch (error) {
      console.error('Booking error:', error);
      toast.error('Failed to create booking');
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }
  
  // Success Step
  if (step === 6) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <CardContent className="pt-8 pb-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Booking Confirmed!</h2>
            <p className="text-gray-500 mb-6">
              You'll receive a confirmation email shortly.
            </p>
            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={() => navigate('/')}>
                Home
              </Button>
              <Button onClick={() => navigate('/music')}>
                Browse More
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-6 px-4">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Music className="w-6 h-6" />
            Book Music Service
          </h1>
          {provider && (
            <p className="text-indigo-200">with {provider.display_name}</p>
          )}
        </div>
      </div>
      
      <div className="max-w-3xl mx-auto px-4 py-8">
        <StepProgress currentStep={step} />
        
        {/* Step 1: Service Selection */}
        {step === 1 && (
          <Card>
            <CardHeader>
              <CardTitle>Select Service</CardTitle>
              <CardDescription>Choose the service you need</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <RadioGroup value={serviceType} onValueChange={setServiceType}>
                {Object.entries(SERVICE_TYPES).map(([key, service]) => (
                  <label 
                    key={key}
                    className={`flex items-center gap-4 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      serviceType === key ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <RadioGroupItem value={key} />
                    <span className="text-2xl">{service.icon}</span>
                    <span className="font-medium">{service.name}</span>
                  </label>
                ))}
              </RadioGroup>
              
              {serviceType && (
                <div className="pt-4 border-t">
                  <Label>Duration</Label>
                  <Select value={duration.toString()} onValueChange={(v) => setDuration(parseInt(v))}>
                    <SelectTrigger className="mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICE_TYPES[serviceType]?.durations.map(d => (
                        <SelectItem key={d} value={d.toString()}>
                          {d} minutes ({d / 60} hour{d > 60 ? 's' : ''})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <Button 
                className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 mt-4"
                onClick={() => setStep(2)}
                disabled={!serviceType}
              >
                Continue
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}
        
        {/* Step 2: Details */}
        {step === 2 && (
          <Card>
            <CardHeader>
              <CardTitle>Service Details</CardTitle>
              <CardDescription>Tell us more about your needs</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {serviceType === 'lessons' && (
                <div>
                  <Label>What instrument?</Label>
                  <Select value={instrument} onValueChange={setInstrument}>
                    <SelectTrigger className="mt-2">
                      <SelectValue placeholder="Select instrument" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="guitar">Guitar</SelectItem>
                      <SelectItem value="piano">Piano/Keyboard</SelectItem>
                      <SelectItem value="drums">Drums</SelectItem>
                      <SelectItem value="violin">Violin</SelectItem>
                      <SelectItem value="bass">Bass Guitar</SelectItem>
                      <SelectItem value="vocals">Vocals</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <div>
                <Label>Location</Label>
                <RadioGroup value={locationType} onValueChange={setLocationType} className="mt-2">
                  {LOCATION_TYPES.filter(loc => {
                    // Filter based on provider capabilities
                    if (loc.id === 'online' && !provider?.online_lessons) return false;
                    if (loc.id === 'customer_home' && !provider?.home_visits) return false;
                    return true;
                  }).map(loc => {
                    const Icon = loc.icon;
                    return (
                      <label 
                        key={loc.id}
                        className={`flex items-center gap-4 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          locationType === loc.id ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <RadioGroupItem value={loc.id} />
                        <Icon className="w-5 h-5 text-gray-600" />
                        <div>
                          <p className="font-medium">{loc.name}</p>
                          <p className="text-sm text-gray-500">{loc.description}</p>
                        </div>
                      </label>
                    );
                  })}
                </RadioGroup>
              </div>
              
              {locationType === 'customer_home' && (
                <div>
                  <Label>Your Address</Label>
                  <Textarea 
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="Enter your full address"
                    className="mt-2"
                    rows={2}
                  />
                </div>
              )}
              
              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setStep(1)}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button 
                  className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-600"
                  onClick={() => setStep(3)}
                >
                  Continue
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Step 3: Schedule */}
        {step === 3 && (
          <Card>
            <CardHeader>
              <CardTitle>Choose Date & Time</CardTitle>
              <CardDescription>Select your preferred appointment slot</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label className="mb-2 block">Select Date</Label>
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(d) => d < new Date() || d.getDay() === 0}
                    className="rounded-md border"
                  />
                </div>
                
                <div>
                  <Label className="mb-2 block">Select Time</Label>
                  <div className="grid grid-cols-3 gap-2 max-h-72 overflow-y-auto">
                    {TIME_SLOTS.map(slot => (
                      <button
                        key={slot}
                        onClick={() => setTime(slot)}
                        className={`p-2 text-sm rounded-lg border transition-all ${
                          time === slot 
                            ? 'bg-indigo-600 text-white border-indigo-600' 
                            : 'border-gray-200 hover:border-indigo-300'
                        }`}
                      >
                        {slot}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              
              {date && time && (
                <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                  <p className="text-sm text-indigo-800">
                    <strong>Selected:</strong> {date.toLocaleDateString('en-GB', { weekday: 'long', month: 'long', day: 'numeric' })} at {time}
                  </p>
                </div>
              )}
              
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(2)}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button 
                  className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-600"
                  onClick={() => setStep(4)}
                  disabled={!date || !time}
                >
                  Continue
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Step 4: Contact Details */}
        {step === 4 && (
          <Card>
            <CardHeader>
              <CardTitle>Your Contact Details</CardTitle>
              <CardDescription>How can the provider reach you?</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Full Name *</Label>
                <Input 
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="John Smith"
                  className="mt-2"
                />
              </div>
              
              <div>
                <Label>Email Address *</Label>
                <Input 
                  type="email"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  placeholder="john@example.com"
                  className="mt-2"
                />
              </div>
              
              <div>
                <Label>Phone Number</Label>
                <Input 
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  placeholder={country === 'LK' ? '+94 7x xxx xxxx' : '+44 7xxx xxxxxx'}
                  className="mt-2"
                />
              </div>
              
              <div>
                <Label>Additional Notes</Label>
                <Textarea 
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any special requests or information for the provider..."
                  className="mt-2"
                  rows={3}
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setStep(3)}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button 
                  className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-600"
                  onClick={() => setStep(5)}
                  disabled={!customerName || !customerEmail}
                >
                  Review & Pay
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Step 5: Payment / Review */}
        {step === 5 && (
          <Card>
            <CardHeader>
              <CardTitle>Review & Pay</CardTitle>
              <CardDescription>Confirm your booking details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Provider Info */}
              {provider && (
                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                  <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                    <Music className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold">{provider.display_name}</h4>
                    <p className="text-sm text-gray-500 capitalize">{provider.provider_type?.replace('_', ' ')}</p>
                    <div className="flex items-center gap-1 text-sm">
                      <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                      <span>{provider.rating?.toFixed(1)}</span>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Booking Summary */}
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-600">Service</span>
                  <span className="font-medium">{SERVICE_TYPES[serviceType]?.name}</span>
                </div>
                {instrument && (
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-gray-600">Instrument</span>
                    <span className="font-medium capitalize">{instrument}</span>
                  </div>
                )}
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-600">Duration</span>
                  <span className="font-medium">{duration} minutes</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-600">Date & Time</span>
                  <span className="font-medium">
                    {date?.toLocaleDateString('en-GB', { month: 'short', day: 'numeric' })} at {time}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-600">Location</span>
                  <span className="font-medium">{LOCATION_TYPES.find(l => l.id === locationType)?.name}</span>
                </div>
              </div>
              
              {/* Pricing */}
              <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Base Price ({durationHours} hr × {currencySymbol}{basePrice}/hr)</span>
                  <span>{currencySymbol}{(basePrice * durationHours).toFixed(2)}</span>
                </div>
                {homeVisitSurcharge > 0 && (
                  <div className="flex justify-between text-sm">
                    <span>Home Visit Surcharge (20%)</span>
                    <span>{currencySymbol}{homeVisitSurcharge.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg pt-2 border-t border-indigo-200">
                  <span>Total</span>
                  <span className="text-indigo-600">{currencySymbol}{totalPrice.toFixed(2)}</span>
                </div>
              </div>
              
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(4)}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button 
                  className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-600"
                  onClick={handleSubmit}
                  disabled={submitting}
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CreditCard className="w-4 h-4 mr-2" />
                  )}
                  Confirm & Pay {currencySymbol}{totalPrice.toFixed(2)}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
